/**
 * Bundle-shape helpers for decomposable by-component bundle rows.
 *
 * A DECOMPOSABLE meal is ordered as a full meal but shipped
 * component-wise (`!bundle.shipsComplete` with more than one distinct
 * part kind). This module answers the SHAPE questions — which kinds a
 * bundle splits into, what a campus's effective rate is per kind, how to
 * label a kind — plus the percentage formatting the SETTINGS surfaces
 * still use (`formatMealtimeSplitSummary`; rates are authored in percent
 * and that has not changed).
 *
 * The ORDERING surface speaks quantities, not percentages: see
 * ./component-quantities. Percent was a lossy encoding of "how many"
 * (6750 bps → 68% → 136 units for 200 meals, where the server and the
 * milk block both said 135), so nothing on the order sheet derives from
 * it any more.
 *
 * Pure (no React) — unit-tested in `client/test/lib/school-orders/`.
 */

import type { ComponentKind } from '@/api/location-settings'
import { bpsToPercentString, COMPONENT_KINDS, componentKindLabel, SPLIT_COMPONENT_KINDS } from '@/api/location-settings'

import { foldedColumnKeyFor, rateRowColumnKey } from './milk'

export interface BundlePartWire {
  componentKind: string
  isBulk: boolean
}

/**
 * One delivery-rate row off `/api/location-settings/:id/delivery-rates`,
 * reduced to what the effective-rate merge needs. Scoped by the
 * `(groupDimension, groupValue)` order-column tuple (null/null =
 * location-wide) and optionally by one mealtime (null/absent = the
 * legacy all-mealtimes fallback).
 */
export interface DeliveryRateWire {
  componentKind: string
  deliveryRateBps: number
  groupDimension: string | null
  groupValue: string | null
  mealtime?: string | null
}

/**
 * The effective bps per component kind for one (order column, mealtime):
 * overlay passes, least → most specific — (legacy, wide) → (legacy, FOLD) →
 * (legacy, column) → (mealtime, wide) → (mealtime, FOLD) → (mealtime, column)
 * — later passes overwrite per kind. Missing kinds stay absent (= 100%, i.e.
 * one per meal to `componentQtyFromRate`). Omitting the mealtime keeps the
 * legacy two-tier behavior (mealtime-scoped rows invisible). Client mirror of
 * `DeliveryRateResolver.getRates`.
 *
 * `foldedColumnIds` are the campus grade-range column keys a menu-derived BAND
 * column falls back to (`OrderColumn.coveredLocationGradeRangeIds`) — see
 * `foldedColumnKeyFor` in ./milk. Omit them and resolution is unchanged.
 */
export function effectiveRatesFor(
  rates: readonly DeliveryRateWire[],
  columnId: string | null,
  mealtime?: string | null,
  foldedColumnIds?: readonly string[],
): Partial<Record<ComponentKind, number>> {
  const isLegacy = (row: DeliveryRateWire) => (row.mealtime ?? null) === null
  const isMealtime = (row: DeliveryRateWire) => mealtime != null && row.mealtime === mealtime
  const passes: Array<(row: DeliveryRateWire) => boolean> = []
  for (const onAxis of [isLegacy, isMealtime]) {
    passes.push((row) => onAxis(row) && rateRowColumnKey(row) === null)
    const foldKey = columnId === null ? null : foldedColumnKeyFor(rates, foldedColumnIds, onAxis)
    if (foldKey !== null && foldKey !== columnId) {
      passes.push((row) => onAxis(row) && rateRowColumnKey(row) === foldKey)
    }
    passes.push((row) => onAxis(row) && columnId !== null && rateRowColumnKey(row) === columnId)
  }
  const effective: Partial<Record<ComponentKind, number>> = {}
  for (const matches of passes) {
    for (const row of rates) {
      if (matches(row)) {
        effective[row.componentKind as ComponentKind] = row.deliveryRateBps
      }
    }
  }
  return effective
}

/**
 * Human label for a ComponentKind for the percentage surfaces —
 * `componentKindLabel` for known kinds ("BEVERAGE" → "Milk", "FRUIT" →
 * "Fruit/Juice"), string-mangling fallback for unknown wire values.
 */
export function componentKindDisplayLabel(kind: string): string {
  if ((COMPONENT_KINDS as readonly string[]).includes(kind)) {
    return componentKindLabel(kind as ComponentKind)
  }
  return kind
    .toLowerCase()
    .split('_')
    .map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

/**
 * The unique SPLIT-VISIBLE kinds of a bundle's parts, MAIN_ENTREE first,
 * otherwise in first-seen order. These are the split editor's rows.
 * Hidden kinds (SIDE, SUPPLEMENT) implicitly ship at 100% and never get
 * a row — a bundle whose only non-main parts are hidden kinds is not
 * decomposable.
 */
export function splitKinds(parts: readonly BundlePartWire[]): ComponentKind[] {
  const seen = new Set<string>()
  const kinds: ComponentKind[] = []
  for (const part of parts) {
    if (!seen.has(part.componentKind) && (SPLIT_COMPONENT_KINDS as readonly string[]).includes(part.componentKind)) {
      seen.add(part.componentKind)
      kinds.push(part.componentKind as ComponentKind)
    }
  }
  return kinds.sort((a, b) => (a === 'MAIN_ENTREE' ? -1 : b === 'MAIN_ENTREE' ? 1 : 0))
}

/**
 * Read-only "which condiments ship with each meal" line under the
 * CONDIMENT split row (cms1d1k7u00cy016cnz7zrvmt): names joined with
 * " · ", per-meal quantities > 1 rendered as "×N".
 */
export function condimentListLabel(condiments: ReadonlyArray<{ name: string; quantity: number }>): string {
  return condiments.map((c) => (c.quantity > 1 ? `${c.name} ×${c.quantity}` : c.name)).join(' · ')
}

/**
 * Whether an item is decomposable at all — a meal ordered whole but
 * shipped component-wise. OVS columns only; single-kind bundles have
 * nothing to split. "Ships complete" packaging does NOT exempt a bundle:
 * the campus's split percentages apply to its components regardless of
 * packaging (component ordering is not a separate mode).
 */
export function isDecomposable(args: { serviceStyle: 'OVS' | 'SS' | null; parts: readonly BundlePartWire[] }): boolean {
  return args.serviceStyle === 'OVS' && splitKinds(args.parts).length > 1
}

/**
 * The Pre-fill settings card's per-mealtime summary line:
 * "Main entrée 100% • Fruit/Juice 60–70% • Vegetable 50% • …". Resolve
 * each actual order column, district first and campus over it, so a campus
 * override wins even when the district row is more specific on the mealtime
 * axis. Flattening both scopes into one list can reverse that precedence.
 * A range shows the percentages that apply when the columns disagree;
 * location-wide defaults do not widen it if no order column uses them.
 */
export function formatMealtimeSplitSummary(
  rates: readonly DeliveryRateWire[],
  mealtime: string,
  options: {
    inheritedRates?: readonly DeliveryRateWire[]
    columns?: ReadonlyArray<{ key: string | null; foldedColumnIds?: readonly string[] }>
    kinds?: readonly ComponentKind[]
  } = {},
): string {
  const { inheritedRates = [], kinds = SPLIT_COMPONENT_KINDS } = options
  const columns: NonNullable<typeof options.columns> = options.columns ?? [
    { key: null },
    ...[
      ...new Set(
        [...rates, ...inheritedRates].map((row) => rateRowColumnKey(row)).filter((key): key is string => key !== null),
      ),
    ].map((key) => ({ key })),
  ]
  const resolved = (columns.length > 0 ? columns : [{ key: null }]).map((column) => ({
    ...effectiveRatesFor(inheritedRates, column.key, mealtime, column.foldedColumnIds),
    ...effectiveRatesFor(rates, column.key, mealtime, column.foldedColumnIds),
  }))
  return kinds
    .map((kind) => {
      if (kind === 'MAIN_ENTREE') {
        return `${componentKindLabel(kind)} 100%`
      }
      const bps = resolved.map((column) => column[kind] ?? 10000)
      const low = Math.min(...bps)
      const high = Math.max(...bps)
      const value = low === high ? bpsToPercentString(low) : `${bpsToPercentString(low)}–${bpsToPercentString(high)}`
      return `${componentKindLabel(kind)} ${value}%`
    })
    .join(' • ')
}
